import { deriveRulesetLogicFromCriteria, normalizeSegmentLogic } from '@/utils/segmentRuleset'

/**
 * Build a stable "canonical" segment array for comparisons (published vs current).
 * This intentionally keeps the shape aligned with backend segment config:
 * { id, name, priority, custom, ruleset: { logic, rules } }
 */

type CanonicalSegment = {
  id: string
  name: string
  priority: number
  custom: boolean
  ruleset: { logic: 'AND' | 'OR'; rules: any[] }
}

type SegmentRuleNode =
  | {
      type: 'rule'
      field?: string
      operator?: string
      value?: unknown
    }
  | {
      type: 'group'
      logic?: 'AND' | 'OR'
      rules?: SegmentRuleNode[]
    }

function normalizeScalarForSig(v: unknown): string {
  if (v === null || v === undefined) return ''
  if (typeof v === 'string') return v
  try {
    return typeof v === 'object' ? JSON.stringify(v) : String(v)
  } catch {
    return String(v)
  }
}

/**
 * Normalize rule nodes for stable comparisons.
 * - Drops non-semantic fields (e.g., action/reason/metadata) if present
 * - Produces stable key order
 * - Best-effort sorts rule arrays so backend reordering doesn't create false "modified"
 */
function normalizeRuleNode(node: any): SegmentRuleNode | null {
  if (!node || typeof node !== 'object') return null

  if (node.type === 'group') {
    const normalizedChildren: Array<SegmentRuleNode | null> = Array.isArray(node.rules)
      ? (node.rules as any[]).map(normalizeRuleNode)
      : []
    const children = normalizedChildren.filter((c: SegmentRuleNode | null): c is SegmentRuleNode => !!c)
    const sortedChildren = children.sort((a: SegmentRuleNode, b: SegmentRuleNode) =>
      JSON.stringify(a).localeCompare(JSON.stringify(b)),
    )

    return {
      type: 'group',
      logic: node.logic === 'OR' ? 'OR' : 'AND',
      rules: sortedChildren,
    }
  }

  // Default to leaf "rule" if type is missing but fields exist
  const type: 'rule' = 'rule'
  const field = node.field
  const operator = node.operator
  const value = node.value

  return {
    type,
    field,
    operator,
    value,
  }
}

function normalizeRuleset(ruleset: any): { logic: 'AND' | 'OR'; rules: SegmentRuleNode[] } {
  const logic: 'AND' | 'OR' = ruleset?.logic === 'OR' ? 'OR' : 'AND'
  const normalized = Array.isArray(ruleset?.rules) ? ruleset.rules.map(normalizeRuleNode).filter(Boolean) : []

  // If backend emits single-rule groups or different ordering, normalize order by signature.
  const sorted = (normalized as SegmentRuleNode[]).sort((a, b) => {
    const aSig =
      a.type === 'rule'
        ? `rule|${normalizeScalarForSig(a.field)}|${normalizeScalarForSig(a.operator)}|${normalizeScalarForSig(a.value)}`
        : `group|${normalizeScalarForSig(a.logic)}|${normalizeScalarForSig(a.rules)}`
    const bSig =
      b.type === 'rule'
        ? `rule|${normalizeScalarForSig(b.field)}|${normalizeScalarForSig(b.operator)}|${normalizeScalarForSig(b.value)}`
        : `group|${normalizeScalarForSig(b.logic)}|${normalizeScalarForSig(b.rules)}`
    return aSig.localeCompare(bSig)
  })

  return { logic, rules: sorted }
}

/**
 * Normalize priority to be "order-based" (10/20/30…) rather than trusting raw numeric values.
 *
 * Why: Saving groups renumbers priorities. If published uses a different numeric scheme,
 * raw comparisons will incorrectly mark every segment as modified.
 */
function normalizePriorityByOrder(segs: CanonicalSegment[]): CanonicalSegment[] {
  const all = segs.find((s) => s.id === 'all') || null

  const nonAll = segs
    .filter((s) => s.id !== 'all')
    // Use existing priority only to establish order, then rewrite priority to a stable rank.
    .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999) || a.id.localeCompare(b.id))

  nonAll.forEach((s, idx) => {
    s.priority = (idx + 1) * 10
  })
  if (all) all.priority = 1000

  return all ? [...nonAll, all] : nonAll
}

export function canonicalizePublishedSegments(publishedSegmentsRaw: any): any[] {
  if (!publishedSegmentsRaw || typeof publishedSegmentsRaw !== 'object') return []

  const segs: CanonicalSegment[] = []

  for (const [id, seg] of Object.entries(publishedSegmentsRaw as any)) {
    const s: any = seg || {}
    segs.push({
      id,
      name: s.name,
      priority:
        typeof s.priority === 'number'
          ? s.priority
          : id === 'all'
            ? 1000
            : 999,
      custom: s.custom ?? false,
      ruleset: normalizeRuleset(s.ruleset || { logic: 'AND', rules: [] }),
    })
  }

  return normalizePriorityByOrder(segs)
}

export function canonicalizeCurrentSegments(currentSegments: any[]): any[] {
  const current = Array.isArray(currentSegments) ? currentSegments : []

  const segs: CanonicalSegment[] = current.map((s: any) => {
    const derivedLogic = normalizeSegmentLogic(deriveRulesetLogicFromCriteria(s?.criteria as any, 'AND'), 'AND')

    return {
      id: String(s?.id || ''),
      name: s?.name,
      priority:
        typeof s?.priority === 'number'
          ? s.priority
          : s?.id === 'all'
            ? 1000
            : 999,
      custom: s?.custom ?? false,
      ruleset: normalizeRuleset({
        logic: derivedLogic,
        rules: (s?.criteria || [])
          .filter((c: any) => c?.field && c?.operator)
          .map((c: any) => ({
            type: 'rule',
            field: c.field,
            operator: c.operator,
            value: c.value,
          })),
      }),
    }
  })

  return normalizePriorityByOrder(segs)
}

